import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Stack, Text, useTheme, Box, Avatar } from '@ltvco/refresh-lib/theme';
import { useAccount, useSession } from '@ltvco/refresh-lib/v1';
import {
  Close,
  InsertDriveFile as InsertDriveFileIcon,
} from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { useContext, useState, useRef } from 'react';

import {
  BrandLogoChat,
  NewChatButton,
  ChatPageBoxContainer,
  ProfileModal,
  HelpModal,
} from 'components/ChatBV/components';
import {
  CHATBV_GA4_EVENTS,
  FEEDBACK_FORM_URL,
} from 'components/ChatBV/constants';
import { ChatContext } from 'components/ChatBV/context/ChatContext';

import { ProfileSection } from '../../Sidebar/ProfileSection';

interface ChatPageSidebarMobileProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onRecentReportsClick?: () => void;
  onCloseRecentReports?: () => void;
  onUpgradePlanClick?: () => void;
}

export const ChatPageSidebarMobile = ({
  isOpen,
  setIsOpen,
  onRecentReportsClick,
  onCloseRecentReports,
  onUpgradePlanClick,
}: ChatPageSidebarMobileProps) => {
  const theme = useTheme();
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [isProfileHovered, setIsProfileHovered] = useState(false);
  const { handleNewChat } = useContext(ChatContext);
  const { trackEventGA4 } = useContext(AppConfig);
  const {
    session: { isAuthenticated },
  } = useSession();
  const { data: account } = useAccount(isAuthenticated);
  const profileRef = useRef<HTMLDivElement>(null);

  const handleNewChatClick = () => {
    handleNewChat();
    onCloseRecentReports?.();
    setIsOpen(false);
  };

  const handleRecentReportsClick = () => {
    onRecentReportsClick?.();
    setIsOpen(false); // Close mobile sidebar when opening recent reports
  };

  const handleProfileClick = () => {
    setOpenProfileModal(true);
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: 'profile_modal',
    });
  };

  const handleProfileModalClose = () => {
    setOpenProfileModal(false);
    setIsOpen(false); // Close sidebar when modal closes
  };

  const handleHelpModalClick = () => {
    setOpenHelpModal(true);
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.help_modal,
    });
  };

  const handleFeedbackFormClick = () => {
    window.open(FEEDBACK_FORM_URL, '_blank');
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.feedback_form,
    });
  };

  const handleUpgradePlanClick = () => {
    onUpgradePlanClick?.();
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.navSidebar.type,
      subtype: CHATBV_GA4_EVENTS.navSidebar.subTypes.upgrade_plan,
    });
  };

  const getInitial = () => {
    const firstName = account?.account?.user_info?.first_name;
    const lastName = account?.account?.user_info?.last_name;
    const email = account?.account?.user_info?.email;

    if (firstName) {
      return firstName[0].toUpperCase();
    }
    if (lastName) {
      return lastName[0].toUpperCase();
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <>
      <ChatPageBoxContainer
        position="fixed"
        top={0}
        left={0}
        width="100%"
        height="100vh"
        boxShadow="4px 0 8px rgba(0, 0, 0, 0.15)"
        zIndex={1000}
        isAnimatingOut={!isOpen}
      >
        <Stack spacing={3} padding={3} height="100%">
          <Stack
            alignItems="center"
            spacing={2}
            marginBottom={4}
            flexDirection="row"
            justifyContent="space-between"
            width="100%"
          >
            <BrandLogoChat width="116px" height="16px" />
            <Close
              onClick={() => {
                setIsOpen(false);
              }}
              sx={{
                marginTop: '0 !important',
              }}
            />
          </Stack>

          <Stack spacing={2} flex={1}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{
                marginBottom: '32px !important',
              }}
              onClick={handleNewChatClick}
            >
              <NewChatButton variant="contained">
                <AddIcon
                  sx={{
                    width: '20px',
                    height: '20px',
                    color: theme.palette.text.contrastText,
                  }}
                />
              </NewChatButton>
              <Text
                variant="body1"
                fontWeight="bold"
                color={theme.palette.primary.main}
              >
                New Chat
              </Text>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{
                marginBottom: '32px !important',
              }}
              onClick={handleRecentReportsClick}
            >
              <InsertDriveFileIcon
                sx={{
                  width: '20px',
                  height: '20px',
                  color: theme.palette.text.secondary,
                }}
              />
              <Text
                variant="body1"
                fontWeight="bold"
                color={theme.palette.primary.main}
              >
                Report Library
              </Text>
            </Stack>
          </Stack>

          <Stack spacing={2}>
            {/* Profile Section */}
            <Box
              ref={profileRef}
              onClick={handleProfileClick}
              onMouseEnter={() => setIsProfileHovered(true)}
              onMouseLeave={() => setIsProfileHovered(false)}
              sx={{
                padding: '8px',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    backgroundColor: 'primary.main',
                    fontSize: '12px',
                    width: '24px',
                    height: '24px',
                  }}
                >
                  {getInitial()}
                </Avatar>
                <ProfileSection
                  account={account}
                  isHovered={isProfileHovered}
                />
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </ChatPageBoxContainer>
      <HelpModal open={openHelpModal} onClose={() => setOpenHelpModal(false)} />
      <ProfileModal
        open={openProfileModal}
        onClose={handleProfileModalClose}
        onUpgradePlanClick={handleUpgradePlanClick}
        onHelpClick={handleHelpModalClick}
        onFeedbackClick={handleFeedbackFormClick}
      />
    </>
  );
};
